/**
 * MentorVerification.jsx — Mentor Application Form (Production)
 *
 * Changes from original:
 *  ✅ Aadhaar removed entirely
 *  ✅ LinkedIn OAuth integration (pre-fills personal info)
 *  ✅ LinkedIn data editable before submission
 *  ✅ LinkedIn verification badge shown when verified
 *  ✅ All existing multi-step form logic preserved
 *  ✅ Private document upload (mentor-documents bucket)
 *  ✅ Bank details → stored in admin_note JSON
 *  ✅ Availability table preserved
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../firebase";
import { supabase } from "../supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const CATEGORIES = [
  { value: "mental_health",   label: "Mental Health",     icon: "🧠", desc: "Depression, anxiety, trauma, therapy" },
  { value: "career",          label: "Career Guidance",   icon: "💼", desc: "Job search, interviews, career change" },
  { value: "relationships",   label: "Relationships",     icon: "💑", desc: "Couples, family, friendships" },
  { value: "stress",          label: "Stress & Anxiety",  icon: "🌊", desc: "Burnout, panic, work-life balance" },
  { value: "grief",           label: "Grief & Loss",      icon: "🕊️", desc: "Bereavement, life transitions" },
  { value: "addiction",       label: "Addiction Support", icon: "🤝", desc: "Substance, behavioral, recovery" },
  { value: "parenting",       label: "Parenting",         icon: "👨‍👩‍👧", desc: "Child development, co-parenting" },
  { value: "student",         label: "Student Wellness",  icon: "📚", desc: "Academic stress, peer pressure" },
  { value: "general",         label: "General Wellness",  icon: "🌿", desc: "Life coaching, mindfulness, growth" },
];

const STEPS = ["Personal", "Professional", "Documents", "Bank Details", "Review"];
const DAYS  = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIMES = ["06:00","07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00"];

const defaultAvailability = DAYS.map((day, i) => ({
  day, dayNum: i + 1, enabled: i < 5, startTime: "09:00", endTime: "17:00",
}));

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  .mv-root{font-family:'DM Sans',sans-serif;min-height:100vh;background:linear-gradient(135deg,#f0f4ff 0%,#fef6f0 100%);padding:0 0 80px;-webkit-font-smoothing:antialiased}
  .mv-hero{background:linear-gradient(135deg,#3949ab 0%,#5c6bc0 60%,#c95b3d 100%);padding:32px 16px 48px;text-align:center;color:#fff;position:relative;overflow:hidden}
  .mv-hero::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse at 80% 20%,rgba(255,255,255,.08) 0%,transparent 60%)}
  .mv-hero-emoji{font-size:2.5rem;display:block;margin-bottom:10px}
  .mv-hero h1{font-family:'DM Serif Display',serif;font-size:clamp(1.6rem,4vw,2.4rem);font-weight:400;margin-bottom:8px;position:relative}
  .mv-hero p{font-size:.9rem;opacity:.85;max-width:480px;margin:0 auto;line-height:1.6;position:relative}
  .mv-steps-wrap{background:#fff;border-bottom:1px solid #e8eaf6;padding:16px;overflow-x:auto;-webkit-overflow-scrolling:touch}
  .mv-steps{display:flex;max-width:680px;margin:0 auto;min-width:max-content}
  .mv-step-item{display:flex;flex-direction:column;align-items:center;gap:6px;flex:1;min-width:80px;position:relative}
  .mv-step-item:not(:last-child)::after{content:'';position:absolute;top:16px;left:60%;width:80%;height:2px;background:#e8eaf6;z-index:0}
  .mv-step-item.done::after{background:#5c6bc0}
  .mv-step-dot{width:32px;height:32px;border-radius:50%;background:#e8eaf6;color:#999;font-size:.8rem;font-weight:700;display:flex;align-items:center;justify-content:center;position:relative;z-index:1;transition:all .3s}
  .mv-step-item.active .mv-step-dot{background:#5c6bc0;color:#fff;box-shadow:0 0 0 4px rgba(92,107,192,.2)}
  .mv-step-item.done .mv-step-dot{background:#5c6bc0;color:#fff}
  .mv-step-label{font-size:.72rem;font-weight:600;color:#999;white-space:nowrap}
  .mv-step-item.active .mv-step-label,.mv-step-item.done .mv-step-label{color:#5c6bc0}
  .mv-card{max-width:680px;margin:24px auto;background:#fff;border-radius:20px;box-shadow:0 4px 24px rgba(92,107,192,.1);padding:24px 20px;position:relative}
  @media(min-width:600px){.mv-card{padding:32px 36px;margin:28px auto}}
  .mv-card-title{font-family:'DM Serif Display',serif;font-size:1.4rem;font-weight:400;color:#1a1a2e;margin-bottom:4px}
  .mv-card-sub{font-size:.85rem;color:#666;margin-bottom:24px;line-height:1.6}
  .mv-form-grid{display:grid;grid-template-columns:1fr;gap:16px}
  @media(min-width:540px){.mv-form-grid{grid-template-columns:1fr 1fr}}
  .mv-form-grid .full{grid-column:1/-1}
  .mv-field{display:flex;flex-direction:column;gap:6px}
  .mv-label{font-size:.78rem;font-weight:600;color:#555;text-transform:uppercase;letter-spacing:.04em;display:flex;align-items:center;gap:4px}
  .mv-label .req{color:#f44336}
  .mv-input,.mv-select,.mv-textarea{padding:11px 14px;border:1.5px solid #e8eaf6;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:.92rem;color:#1a1a2e;background:#f8f9ff;outline:none;transition:border-color .2s,background .2s;width:100%}
  .mv-input:focus,.mv-select:focus,.mv-textarea:focus{border-color:#5c6bc0;background:#fff}
  .mv-input.err,.mv-select.err{border-color:#f44336}
  .mv-err{font-size:.76rem;color:#f44336;display:flex;align-items:center;gap:4px}
  .mv-hint{font-size:.76rem;color:#999;line-height:1.5}
  .mv-cat-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  @media(min-width:480px){.mv-cat-grid{grid-template-columns:repeat(3,1fr)}}
  .mv-cat-item{padding:12px 10px;border:1.5px solid #e8eaf6;border-radius:12px;cursor:pointer;text-align:center;transition:all .15s;background:#fafbff}
  .mv-cat-item:hover{border-color:#5c6bc0;background:#f0f4ff}
  .mv-cat-item.selected{border-color:#5c6bc0;background:#e8eaf6}
  .mv-cat-icon{font-size:1.4rem;display:block;margin-bottom:4px}
  .mv-cat-name{font-size:.78rem;font-weight:700;color:#1a1a2e;display:block}
  .mv-cat-desc{font-size:.68rem;color:#888;margin-top:2px;display:block;line-height:1.3}
  .mv-price-display{text-align:center;font-size:1.6rem;font-weight:700;color:#5c6bc0;margin:8px 0}
  .mv-price-display span{font-size:.85rem;color:#999;font-weight:400}
  input[type=range].mv-slider{-webkit-appearance:none;width:100%;height:6px;border-radius:3px;background:linear-gradient(to right,#5c6bc0 var(--val,50%),#e8eaf6 var(--val,50%));outline:none;cursor:pointer}
  input[type=range].mv-slider::-webkit-slider-thumb{-webkit-appearance:none;width:20px;height:20px;border-radius:50%;background:#5c6bc0;box-shadow:0 2px 8px rgba(92,107,192,.4)}
  .mv-price-ticks{display:flex;justify-content:space-between;font-size:.72rem;color:#999;margin-top:4px}
  .mv-upload{border:2px dashed #c5cae9;border-radius:12px;padding:20px;text-align:center;cursor:pointer;transition:all .2s;position:relative;background:#f8f9ff}
  .mv-upload:hover{border-color:#5c6bc0;background:#f0f4ff}
  .mv-upload.uploaded{border-color:#4caf50;background:#f1f8e9;border-style:solid}
  .mv-upload-icon{font-size:2rem;display:block;margin-bottom:8px}
  .mv-upload-title{font-weight:600;font-size:.9rem;color:#1a1a2e;margin-bottom:4px}
  .mv-upload-hint{font-size:.78rem;color:#888}
  .mv-upload input[type=file]{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}
  .mv-upload-success{display:flex;align-items:center;gap:8px;color:#2e7d32;font-size:.85rem;font-weight:600}
  .mv-avail-table{width:100%;border-collapse:collapse}
  .mv-avail-table th{font-size:.75rem;font-weight:700;color:#666;text-transform:uppercase;padding:6px 8px;border-bottom:2px solid #e8eaf6;text-align:left}
  .mv-avail-table td{padding:6px 8px;border-bottom:1px solid #f5f5f5;vertical-align:middle}
  .mv-avail-table input[type=checkbox]{width:16px;height:16px;accent-color:#5c6bc0;cursor:pointer}
  .mv-avail-table select{padding:4px 8px;border:1px solid #e8eaf6;border-radius:6px;font-size:.82rem;background:#f8f9ff}
  .mv-review-section{margin-bottom:20px}
  .mv-review-section h4{font-size:.85rem;font-weight:700;color:#5c6bc0;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #e8eaf6}
  .mv-review-row{display:flex;justify-content:space-between;align-items:flex-start;padding:6px 0;font-size:.875rem;border-bottom:1px solid #f5f5f5}
  .mv-review-label{color:#888;font-weight:500;flex-shrink:0;min-width:140px}
  .mv-review-value{color:#1a1a2e;font-weight:600;text-align:right;word-break:break-all}
  .mv-consent{background:#f0f4ff;border:1px solid #c5cae9;border-radius:10px;padding:14px;font-size:.82rem;color:#555;line-height:1.7}
  .mv-consent label{display:flex;align-items:flex-start;gap:10px;cursor:pointer}
  .mv-consent input[type=checkbox]{width:16px;height:16px;flex-shrink:0;margin-top:2px;accent-color:#5c6bc0}
  .mv-nav{display:flex;gap:10px;margin-top:28px}
  .mv-back-btn{padding:12px 20px;background:#f5f5f5;color:#666;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-weight:600;font-size:.9rem;cursor:pointer;transition:background .2s}
  .mv-back-btn:hover{background:#e8eaf6;color:#1a1a2e}
  .mv-next-btn{flex:1;padding:13px;background:linear-gradient(135deg,#5c6bc0,#3949ab);color:#fff;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-weight:700;font-size:.95rem;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 4px 12px rgba(92,107,192,.3);transition:opacity .2s,transform .2s}
  .mv-next-btn:hover:not(:disabled){opacity:.9;transform:translateY(-1px)}
  .mv-next-btn:disabled{opacity:.5;cursor:not-allowed;transform:none}
  .mv-spin{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:mv-spin .6s linear infinite}
  @keyframes mv-spin{to{transform:rotate(360deg)}}
  .mv-alert{padding:12px 16px;border-radius:10px;font-size:.855rem;line-height:1.55;display:flex;align-items:flex-start;gap:10px;animation:mv-fade .22s ease}
  @keyframes mv-fade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
  .mv-alert.error{background:#ffebee;border:1px solid #ffcdd2;color:#c62828}
  .mv-alert.success{background:#f1f8e9;border:1px solid #c8e6c9;color:#2e7d32}
  .mv-alert.info{background:#e8eaf6;border:1px solid #c5cae9;color:#3949ab}
  .mv-success{text-align:center;padding:40px 20px}
  .mv-success-icon{width:80px;height:80px;border-radius:50%;background:linear-gradient(135deg,#e8f5e9,#f1f8e9);border:2px solid #a5d6a7;display:flex;align-items:center;justify-content:center;font-size:2.2rem;margin:0 auto 20px;animation:mv-bounce .5s cubic-bezier(.22,.68,0,1.55) both}
  @keyframes mv-bounce{from{transform:scale(.3);opacity:0}to{transform:scale(1);opacity:1}}
  .mv-success h2{font-family:'DM Serif Display',serif;font-size:1.8rem;color:#1a1a2e;margin-bottom:10px}
  .mv-success p{font-size:.9rem;color:#666;line-height:1.7;max-width:400px;margin:0 auto 24px}
  .mv-timeline{text-align:left;display:flex;flex-direction:column;gap:12px}
  .mv-tl-item{display:flex;align-items:flex-start;gap:12px;font-size:.875rem;color:#555}
  .mv-tl-dot{width:28px;height:28px;border-radius:50%;background:#5c6bc0;color:#fff;font-size:.72rem;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px}

  /* LinkedIn badge */
  .mv-linkedin-badge{display:inline-flex;align-items:center;gap:8px;padding:8px 16px;background:#e8f4fd;border:1.5px solid #0077b5;border-radius:10px;font-size:.85rem;font-weight:600;color:#0077b5;margin-bottom:16px}
  .mv-linkedin-btn{width:100%;padding:13px;background:#0077b5;color:#fff;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-weight:700;font-size:.95rem;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:background .2s;margin-bottom:16px}
  .mv-linkedin-btn:hover{background:#005885}
  @media(max-width:400px){.mv-card{margin:16px 12px;padding:20px 16px}}
`;

export default function MentorVerification() {
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();

  const [step,        setStep]        = useState(0);
  const [submitted,   setSubmitted]   = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [alert,       setAlert]       = useState(null);
  const [errs,        setErrs]        = useState({});
  const [linkedinVerified, setLinkedinVerified] = useState(false);

  // ── Personal ────────────────────────────────────────────────
  const [personal, setPersonal] = useState({
    fullName: auth.currentUser?.displayName || "",
    email:    auth.currentUser?.email       || "",
    phone:    "",
    city:     "",
    linkedinId: "",
    linkedinPhoto: "",
  });

  // ── Professional ────────────────────────────────────────────
  const [professional, setProfessional] = useState({
    profession:   "",
    organization: "",
    experience:   "",
    expertise:    "",
    category:     "",
    sessionPrice: 200,
    bio:          "",
    languages:    "",
  });

  const [availability, setAvailability] = useState(defaultAvailability);

  // ── Documents ───────────────────────────────────────────────
  const [degreeFile, setDegreeFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading,  setUploading]  = useState(false);
  const [degreeUrl,  setDegreeUrl]  = useState("");
  const [resumeUrl,  setResumeUrl]  = useState("");

  // ── Bank ────────────────────────────────────────────────────
  const [bank, setBank] = useState({
    accountNumber: "", confirmAccount: "", ifsc: "", accountName: "", bankName: "",
  });

  const [consent, setConsent] = useState(false);

  // ── Read LinkedIn data from URL params (backend redirects here directly) ─
  useEffect(() => {
    // Backend redirects to /mentor-verification?linkedin_data=... directly
    const data  = searchParams.get("linkedin_data");
    const error = searchParams.get("linkedin_error");

    if (data) {
      try {
        // base64url decode
        const decoded = JSON.parse(atob(data.replace(/-/g, "+").replace(/_/g, "/")));
        setPersonal(p => ({
          ...p,
          fullName:      decoded.fullName   || p.fullName,
          email:         decoded.email      || p.email,
          linkedinId:    decoded.linkedinId || "",
          linkedinPhoto: decoded.photo      || "",
        }));
        setLinkedinVerified(true);
        // Clean URL so data isn't visible
        window.history.replaceState({}, "", "/mentor-verification");
      } catch (e) {
        console.error("LinkedIn data decode error:", e);
        setAlert({ type: "error", msg: "LinkedIn verification failed. Please try again." });
      }
    }

    if (error) {
      setAlert({ type: "error", msg: "LinkedIn verification failed. Please try again." });
      window.history.replaceState({}, "", "/mentor-verification");
    }
  }, []);

  const up      = (setter, key, val) => setter(p => ({ ...p, [key]: val }));
  const clrErr  = (k) => setErrs(p => ({ ...p, [k]: "" }));

  // ── Validate ─────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (step === 0) {
      // LinkedIn is mandatory — hard gate
      if (!linkedinVerified) {
        setAlert({ type: "error", msg: "LinkedIn verification is required to apply as a mentor. Please verify your LinkedIn profile first." });
        return false;
      }
      if (!personal.fullName.trim()) e.fullName = "Required";
      if (!personal.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personal.email)) e.email = "Valid email required";
      if (!personal.phone.match(/^[6-9]\d{9}$/)) e.phone = "Valid 10-digit Indian mobile required";
      if (!personal.city.trim()) e.city = "Required";
    }
    if (step === 1) {
      if (!professional.profession.trim())  e.profession   = "Required";
      if (!professional.organization.trim()) e.organization = "Required";
      if (!professional.experience || isNaN(professional.experience)) e.experience = "Valid years required";
      if (!professional.expertise.trim())   e.expertise    = "Required";
      if (!professional.category)           e.category     = "Please select a category";
      if (!professional.bio.trim() || professional.bio.length < 80) e.bio = "Bio must be at least 80 characters";
    }
    if (step === 2) {
      if (!degreeUrl) e.degree = "Degree certificate required";
      if (!resumeUrl) e.resume = "Resume / CV required";
    }
    if (step === 3) {
      if (!bank.accountNumber.match(/^\d{9,18}$/)) e.accountNumber = "Valid account number required";
      if (bank.accountNumber !== bank.confirmAccount) e.confirmAccount = "Account numbers do not match";
      if (!bank.ifsc.match(/^[A-Z]{4}0[A-Z0-9]{6}$/)) e.ifsc = "Valid IFSC required (e.g. SBIN0001234)";
      if (!bank.accountName.trim()) e.accountName = "Required";
    }
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  // ── Upload file ──────────────────────────────────────────────
  const uploadFile = async (file, type) => {
    if (!file) return null;
    const ext  = file.name.split(".").pop();
    const name = `${type}_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const path = `applications/${personal.email}/${name}`;

    const { data, error } = await supabase.storage
      .from("mentor-documents")
      .upload(path, file, { cacheControl: "3600", upsert: true });

    if (error) throw new Error(`Upload failed: ${error.message}`);
    return `mentor-documents/${data.path}`;
  };

  const handleFileSelect = async (file, type) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setAlert({ msg: "File must be under 5MB", type: "error" }); return; }
    if (!["application/pdf","image/jpeg","image/png"].includes(file.type)) {
      setAlert({ msg: "Only PDF, JPG, PNG files allowed", type: "error" }); return;
    }
    setUploading(true); setAlert(null);
    try {
      const url = await uploadFile(file, type);
      if (type === "degree") { setDegreeFile(file); setDegreeUrl(url); clrErr("degree"); }
      else                   { setResumeFile(file);  setResumeUrl(url);  clrErr("resume"); }
    } catch (err) {
      setAlert({ msg: err.message, type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleNext = () => {
    if (!validate()) { setAlert({ msg: "Please fix the errors above.", type: "error" }); return; }
    setAlert(null);
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => { setStep(s => s - 1); setAlert(null); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // ── LinkedIn redirect ────────────────────────────────────────
  const handleLinkedInVerify = () => {
    window.location.href = `${BACKEND_URL}/api/auth/linkedin`;
  };

  // ── Submit ───────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!consent) { setAlert({ msg: "Please accept the terms.", type: "error" }); return; }
    setLoading(true); setAlert(null);
    try {
      const availabilityData = availability
        .filter(a => a.enabled)
        .map(a => ({ day: a.day, dayNum: a.dayNum, startTime: a.startTime, endTime: a.endTime }));

      const { data: application, error: appErr } = await supabase
        .from("mentor_applications")
        .insert({
          full_name:              personal.fullName.trim(),
          email:                  personal.email.trim().toLowerCase(),
          phone:                  personal.phone.trim(),
          city:                   personal.city.trim(),
          profession:             professional.profession.trim(),
          organization:           professional.organization.trim(),
          experience:             parseInt(professional.experience, 10),
          expertise:              professional.expertise.trim(),
          category:               professional.category,
          qualification:          professional.profession,
          highest_qualification:  professional.profession,
          university:             professional.organization,
          degree_certificate_url: degreeUrl,
          resume_url:             resumeUrl,
          linkedin_id:            personal.linkedinId || null,
          linkedin_verified:      linkedinVerified,
          status:                 "pending",
        })
        .select()
        .single();

      if (appErr) throw appErr;

      // Store bank + extra data in admin_note
      await supabase.from("mentor_applications").update({
        admin_note: JSON.stringify({
          bank_account:  bank.accountNumber,
          bank_ifsc:     bank.ifsc.toUpperCase(),
          bank_name:     bank.accountName,
          bank_provider: bank.bankName,
          session_price: professional.sessionPrice,
          bio:           professional.bio,
          languages:     professional.languages,
          category:      professional.category,
          availability:  availabilityData,
          linkedin_photo: personal.linkedinPhoto,
        }),
      }).eq("id", application.id);

      // Notify admin
      try {
        await fetch(`${BACKEND_URL}/api/mentor-application/notify-admin`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            applicantName:  personal.fullName,
            applicantEmail: personal.email,
            applicationId:  application.id,
          }),
        });
      } catch (_) {}

      setSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      setAlert({ msg: err.message || "Submission failed. Please try again.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const sliderPct = ((professional.sessionPrice - 150) / (400 - 150)) * 100;

  // ── Success screen ───────────────────────────────────────────
  if (submitted) return (
    <>
      <style>{css}</style>
      <div className="mv-root">
        <div className="mv-card" style={{ maxWidth: 580, textAlign: "center" }}>
          <div className="mv-success">
            <div className="mv-success-icon">🎉</div>
            <h2>Application Submitted!</h2>
            <p>Our team will review your credentials within 3–5 business days.</p>
            <div className="mv-timeline">
              {[
                ["Document Review", "Our team verifies your credentials"],
                ["Interview", "You'll receive an email to schedule a short video call"],
                ["Onboarding", "After approval, complete your profile and go live"],
              ].map(([title, desc], i) => (
                <div className="mv-tl-item" key={i}>
                  <div className="mv-tl-dot">{i + 1}</div>
                  <div><strong style={{ display: "block", marginBottom: 2 }}>{title}</strong><span>{desc}</span></div>
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/dashboard")}
              style={{ marginTop: 28, padding: "12px 32px", background: "#5c6bc0", color: "#fff", border: "none", borderRadius: 12, fontWeight: 700, fontSize: ".95rem", cursor: "pointer" }}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <div className="mv-root">
        <div className="mv-hero">
          <button onClick={() => navigate("/dashboard")}
            style={{ position:"absolute",top:16,left:16,background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.3)",borderRadius:8,color:"#fff",padding:"7px 14px",fontFamily:"'DM Sans',sans-serif",fontSize:".82rem",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6,zIndex:10 }}>
            ← Back
          </button>
          <span className="mv-hero-emoji">👩‍⚕️</span>
          <h1>Become a HealHub Mentor</h1>
          <p>Share your expertise and help thousands of people navigate life's challenges</p>
        </div>

        <div className="mv-steps-wrap">
          <div className="mv-steps">
            {STEPS.map((label, i) => (
              <div key={i} className={`mv-step-item ${i < step ? "done" : i === step ? "active" : ""}`}>
                <div className="mv-step-dot">{i < step ? "✓" : i + 1}</div>
                <div className="mv-step-label">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mv-card">
          {alert && (
            <div className={`mv-alert ${alert.type}`} style={{ marginBottom: 20 }}>
              <span>{alert.type === "error" ? "⚠️" : alert.type === "success" ? "✅" : "ℹ️"}</span>
              {alert.msg}
            </div>
          )}

          {/* ── STEP 0: Personal ── */}
          {step === 0 && (
            <>
              <div className="mv-card-title">Verify Your Identity</div>
              <div className="mv-card-sub">LinkedIn verification is <strong>required</strong> to apply as a HealHub mentor. This ensures all mentors are real professionals.</div>

              {/* LinkedIn verification — MANDATORY */}
              {linkedinVerified ? (
                <div className="mv-linkedin-badge">
                  <span>✅</span>
                  LinkedIn Verified — your profile has been confirmed. You can edit details below.
                </div>
              ) : (
                <div style={{ background:"#fff3e0",border:"2px solid #ff9800",borderRadius:12,padding:20,marginBottom:20,textAlign:"center" }}>
                  <div style={{ fontSize:"1.5rem",marginBottom:8 }}>🔗</div>
                  <div style={{ fontWeight:700,fontSize:"1rem",color:"#e65100",marginBottom:6 }}>LinkedIn Verification Required</div>
                  <div style={{ fontSize:".85rem",color:"#666",marginBottom:16,lineHeight:1.6 }}>
                    You must verify your LinkedIn profile before filling in the application. This confirms you are a real professional and speeds up approval.
                  </div>
                  <button className="mv-linkedin-btn" onClick={handleLinkedInVerify} style={{ margin:"0 auto",maxWidth:360 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Verify with LinkedIn
                  </button>
                </div>
              )}

              {/* Form fields — only shown after LinkedIn verified */}
              {linkedinVerified && (
                <div className="mv-form-grid">
                  {[
                    { key: "fullName", label: "Full Name", placeholder: "Dr. Priya Sharma", req: true },
                    { key: "email",    label: "Email Address", placeholder: "you@example.com", req: true, type: "email" },
                    { key: "phone",    label: "Mobile Number (India)", placeholder: "9876543210", req: true, type: "tel" },
                    { key: "city",     label: "City / Location", placeholder: "Mumbai, India", req: true },
                  ].map(({ key, label, placeholder, req, type = "text" }) => (
                    <div className="mv-field" key={key}>
                      <label className="mv-label">{label} {req && <span className="req">*</span>}</label>
                      <input
                        className={`mv-input ${errs[key] ? "err" : ""}`}
                        type={type} placeholder={placeholder}
                        value={personal[key]}
                        onChange={e => { up(setPersonal, key, e.target.value); clrErr(key); }}
                      />
                      {errs[key] && <div className="mv-err">⚠ {errs[key]}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── STEP 1: Professional ── */}
          {step === 1 && (
            <>
              <div className="mv-card-title">Professional Details</div>
              <div className="mv-card-sub">Tell us about your expertise and how you'd like to mentor.</div>
              <div className="mv-form-grid">
                <div className="mv-field">
                  <label className="mv-label">Profession / Title <span className="req">*</span></label>
                  <input className={`mv-input ${errs.profession ? "err" : ""}`}
                    placeholder="Clinical Psychologist" value={professional.profession}
                    onChange={e => { up(setProfessional, "profession", e.target.value); clrErr("profession"); }} />
                  {errs.profession && <div className="mv-err">⚠ {errs.profession}</div>}
                </div>
                <div className="mv-field">
                  <label className="mv-label">Organization / Clinic <span className="req">*</span></label>
                  <input className={`mv-input ${errs.organization ? "err" : ""}`}
                    placeholder="NIMHANS / Private Practice" value={professional.organization}
                    onChange={e => { up(setProfessional, "organization", e.target.value); clrErr("organization"); }} />
                  {errs.organization && <div className="mv-err">⚠ {errs.organization}</div>}
                </div>
                <div className="mv-field">
                  <label className="mv-label">Years of Experience <span className="req">*</span></label>
                  <input className={`mv-input ${errs.experience ? "err" : ""}`}
                    type="number" min="0" max="50" placeholder="5" value={professional.experience}
                    onChange={e => { up(setProfessional, "experience", e.target.value); clrErr("experience"); }} />
                  {errs.experience && <div className="mv-err">⚠ {errs.experience}</div>}
                </div>
                <div className="mv-field">
                  <label className="mv-label">Languages Spoken</label>
                  <input className="mv-input" placeholder="English, Hindi, Tamil"
                    value={professional.languages}
                    onChange={e => up(setProfessional, "languages", e.target.value)} />
                </div>
                <div className="mv-field full">
                  <label className="mv-label">Key Areas of Expertise <span className="req">*</span></label>
                  <input className={`mv-input ${errs.expertise ? "err" : ""}`}
                    placeholder="Anxiety, Depression, CBT, Mindfulness (comma separated)"
                    value={professional.expertise}
                    onChange={e => { up(setProfessional, "expertise", e.target.value); clrErr("expertise"); }} />
                  {errs.expertise && <div className="mv-err">⚠ {errs.expertise}</div>}
                </div>
                <div className="mv-field full">
                  <label className="mv-label">Mentoring Category <span className="req">*</span></label>
                  {errs.category && <div className="mv-err" style={{ marginBottom: 8 }}>⚠ {errs.category}</div>}
                  <div className="mv-cat-grid">
                    {CATEGORIES.map(c => (
                      <div key={c.value} className={`mv-cat-item ${professional.category === c.value ? "selected" : ""}`}
                        onClick={() => { up(setProfessional, "category", c.value); clrErr("category"); }}>
                        <span className="mv-cat-icon">{c.icon}</span>
                        <span className="mv-cat-name">{c.label}</span>
                        <span className="mv-cat-desc">{c.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mv-field full">
                  <label className="mv-label">Session Price <span className="req">*</span></label>
                  <div className="mv-price-display">₹{professional.sessionPrice} <span>per session (+ ₹10 platform fee charged to user)</span></div>
                  <input type="range" className="mv-slider" min="150" max="400" step="50"
                    style={{ "--val": `${sliderPct}%` }} value={professional.sessionPrice}
                    onChange={e => up(setProfessional, "sessionPrice", parseInt(e.target.value))} />
                  <div className="mv-price-ticks"><span>₹150</span><span>₹200</span><span>₹250</span><span>₹300</span><span>₹350</span><span>₹400</span></div>
                </div>
                <div className="mv-field full">
                  <label className="mv-label">Bio / About You <span className="req">*</span>{" "}
                    <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: professional.bio.length < 80 ? "#f44336" : "#4caf50" }}>
                      {professional.bio.length}/80 min
                    </span>
                  </label>
                  <textarea className={`mv-textarea ${errs.bio ? "err" : ""}`} rows={5}
                    placeholder="Write a warm, professional introduction…"
                    value={professional.bio}
                    onChange={e => { up(setProfessional, "bio", e.target.value); clrErr("bio"); }} />
                  {errs.bio && <div className="mv-err">⚠ {errs.bio}</div>}
                </div>

                {/* Availability */}
                <div className="mv-field full">
                  <label className="mv-label">Weekly Availability</label>
                  <div className="mv-hint" style={{ marginBottom: 10 }}>Set which days and times users can book sessions.</div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="mv-avail-table">
                      <thead><tr><th>Day</th><th>Available</th><th>From</th><th>To</th></tr></thead>
                      <tbody>
                        {availability.map((a, i) => (
                          <tr key={a.day}>
                            <td style={{ fontWeight: 600, fontSize: ".85rem" }}>{a.day.slice(0, 3)}</td>
                            <td><input type="checkbox" checked={a.enabled}
                              onChange={e => setAvailability(p => p.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))} /></td>
                            <td>
                              <select disabled={!a.enabled} value={a.startTime}
                                onChange={e => setAvailability(p => p.map((x, j) => j === i ? { ...x, startTime: e.target.value } : x))}>
                                {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                            <td>
                              <select disabled={!a.enabled} value={a.endTime}
                                onChange={e => setAvailability(p => p.map((x, j) => j === i ? { ...x, endTime: e.target.value } : x))}>
                                {TIMES.filter(t => t > a.startTime).map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 2: Documents ── */}
          {step === 2 && (
            <>
              <div className="mv-card-title">Upload Documents</div>
              <div className="mv-card-sub">Documents are stored privately — only our verification team can access them.</div>
              <div className="mv-form-grid">
                {[
                  { type: "degree", label: "Degree / Qualification Certificate", icon: "🎓", file: degreeFile, url: degreeUrl, errKey: "degree" },
                  { type: "resume", label: "Resume / CV",                         icon: "📄", file: resumeFile, url: resumeUrl, errKey: "resume" },
                ].map(({ type, label, icon, file, url, errKey }) => (
                  <div className="mv-field full" key={type}>
                    <label className="mv-label">{label} <span className="req">*</span></label>
                    <div className={`mv-upload ${url ? "uploaded" : ""}`}>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                        onChange={e => handleFileSelect(e.target.files[0], type)} disabled={uploading} />
                      {url
                        ? <div className="mv-upload-success">✅ {file?.name || "File uploaded"}</div>
                        : <>
                          <span className="mv-upload-icon">{icon}</span>
                          <div className="mv-upload-title">{uploading ? "Uploading…" : "Tap to upload"}</div>
                          <div className="mv-upload-hint">PDF, JPG, PNG · Max 5MB</div>
                        </>
                      }
                    </div>
                    {errs[errKey] && <div className="mv-err">⚠ {errs[errKey]}</div>}
                  </div>
                ))}
              </div>
              <div className="mv-alert info" style={{ marginTop: 16 }}>
                <span>🔒</span>
                <span>Your documents are encrypted and stored in a private bucket. Never shared with users.</span>
              </div>
            </>
          )}

          {/* ── STEP 3: Bank Details ── */}
          {step === 3 && (
            <>
              <div className="mv-card-title">Bank Details</div>
              <div className="mv-card-sub">Earnings are transferred here after each confirmed session via Razorpay.</div>
              <div className="mv-form-grid">
                {[
                  { key: "accountName",    label: "Account Holder Name", placeholder: "Exactly as on bank records", full: true, req: true },
                  { key: "bankName",       label: "Bank Name",           placeholder: "State Bank of India" },
                  { key: "accountNumber",  label: "Account Number",      placeholder: "9–18 digit number",    req: true },
                  { key: "confirmAccount", label: "Confirm Account No.", placeholder: "Re-enter account number", req: true },
                  { key: "ifsc",           label: "IFSC Code",           placeholder: "SBIN0001234",           req: true },
                ].map(({ key, label, placeholder, full, req }) => (
                  <div className={`mv-field ${full ? "full" : ""}`} key={key}>
                    <label className="mv-label">{label} {req && <span className="req">*</span>}</label>
                    <input className={`mv-input ${errs[key] ? "err" : ""}`}
                      placeholder={placeholder} value={bank[key]} autoComplete="off"
                      onChange={e => { up(setBank, key, key === "ifsc" ? e.target.value.toUpperCase() : e.target.value); clrErr(key); }} />
                    {errs[key] && <div className="mv-err">⚠ {errs[key]}</div>}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── STEP 4: Review ── */}
          {step === 4 && (
            <>
              <div className="mv-card-title">Review & Submit</div>
              <div className="mv-card-sub">Please review your details before submitting.</div>
              <div className="mv-review-section">
                <h4>Personal</h4>
                {[["Name", personal.fullName], ["Email", personal.email], ["Phone", personal.phone], ["City", personal.city],
                  ["LinkedIn", linkedinVerified ? "✅ Verified" : "Not verified"]
                ].map(([k, v]) => (
                  <div className="mv-review-row" key={k}><span className="mv-review-label">{k}</span><span className="mv-review-value">{v}</span></div>
                ))}
              </div>
              <div className="mv-review-section">
                <h4>Professional</h4>
                {[
                  ["Profession", professional.profession], ["Organization", professional.organization],
                  ["Experience", `${professional.experience} years`],
                  ["Category", CATEGORIES.find(c => c.value === professional.category)?.label || professional.category],
                  ["Expertise", professional.expertise], ["Session Price", `₹${professional.sessionPrice}`],
                ].map(([k, v]) => (
                  <div className="mv-review-row" key={k}><span className="mv-review-label">{k}</span><span className="mv-review-value">{v}</span></div>
                ))}
              </div>
              <div className="mv-review-section">
                <h4>Documents</h4>
                <div className="mv-review-row"><span className="mv-review-label">Degree</span><span className="mv-review-value" style={{ color: "#4caf50" }}>✅ Uploaded</span></div>
                <div className="mv-review-row"><span className="mv-review-label">Resume</span><span className="mv-review-value" style={{ color: "#4caf50" }}>✅ Uploaded</span></div>
              </div>
              <div className="mv-review-section">
                <h4>Bank Details</h4>
                <div className="mv-review-row"><span className="mv-review-label">Account Holder</span><span className="mv-review-value">{bank.accountName}</span></div>
                <div className="mv-review-row"><span className="mv-review-label">Account</span><span className="mv-review-value">****{bank.accountNumber.slice(-4)}</span></div>
                <div className="mv-review-row"><span className="mv-review-label">IFSC</span><span className="mv-review-value">{bank.ifsc}</span></div>
              </div>
              <div className="mv-consent">
                <label>
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                  <span>I confirm all information is accurate. I agree to HealHub's <strong>Mentor Terms of Service</strong>, <strong>Privacy Policy</strong>, and <strong>Community Guidelines</strong>.</span>
                </label>
              </div>
            </>
          )}

          {/* Navigation */}
          <div className="mv-nav">
            {step > 0 && (
              <button className="mv-back-btn" onClick={handleBack} disabled={loading || uploading}>← Back</button>
            )}
            {step < 4 ? (
              <button className="mv-next-btn" onClick={handleNext}
                disabled={uploading || (step === 0 && !linkedinVerified)}>
                {uploading ? <><span className="mv-spin" /> Uploading…</> : "Continue →"}
              </button>
            ) : (
              <button className="mv-next-btn" onClick={handleSubmit} disabled={loading || !consent}>
                {loading ? <><span className="mv-spin" /> Submitting…</> : "Submit Application 🚀"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}